import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} VariantPriceData
 * @property {string} priceDisplay
 * @property {string} priceNumber
 * @property {string} compareDisplay
 * @property {boolean} showCompare
 * @property {number} discountPercentage
 */

/**
 * @typedef {Object} VariantsPriceMap
 * @property {boolean} highlight
 * @property {string} priceSymbol
 * @property {boolean} showSalePriceFirst
 * @property {boolean} volumePricing
 * @property {Record<string, VariantPriceData>} variants
 */

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 * @property {HTMLScriptElement} [variantsPriceData]
 */

/**
 * A custom element that displays a product price.
 * On the product page, prices update instantly from embedded variant data (same pattern as cc-product-card).
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  /** @type {VariantsPriceMap | null} */
  #variantsMap = null;

  connectedCallback() {
    super.connectedCallback();
    this.#loadVariantsMap();

    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  #loadVariantsMap() {
    const script = this.refs.variantsPriceData ?? this.querySelector('script[ref="variantsPriceData"]');
    if (!(script instanceof HTMLScriptElement) || !script.textContent) return;

    try {
      this.#variantsMap = JSON.parse(script.textContent);
    } catch {
      this.#variantsMap = null;
    }
  }

  /**
   * Instant price update from pre-rendered Liquid data (cc-product-card pattern).
   * @param {string} variantId
   */
  applyVariant(variantId) {
    if (this.dataset.volumePricing === 'true' || !this.#variantsMap) return;

    const variant = this.#variantsMap.variants[variantId];
    if (!variant) return;

    this.#paintPrice(variant, this.#variantsMap);
  }

  /**
   * @param {VariantPriceData} variant
   * @param {VariantsPriceMap} map
   */
  #paintPrice(variant, map) {
    const { priceContainer } = this.refs;
    if (!priceContainer) return;

    this.#setDiscountBadge(priceContainer, variant);

    if (map.highlight) {
      const numberEl = priceContainer.querySelector('.cc-price-highlight__number');
      const symbolEl = priceContainer.querySelector('.cc-price-highlight__symbol');
      if (symbolEl && map.priceSymbol) symbolEl.textContent = map.priceSymbol;
      if (numberEl) numberEl.textContent = variant.priceNumber;
      this.#setComparePrice(priceContainer, variant, map.showSalePriceFirst);
      return;
    }

    const showSaleFirst = map.showSalePriceFirst;
    const priceEl = priceContainer.querySelector('.price');
    let compareEl = priceContainer.querySelector('.compare-at-price');

    if (variant.showCompare) {
      if (!compareEl) {
        this.#setComparePrice(priceContainer, variant, showSaleFirst);
        compareEl = priceContainer.querySelector('.compare-at-price');
      } else {
        compareEl.textContent = variant.compareDisplay;
        compareEl.closest('[role="group"]')?.removeAttribute('hidden');
      }
      if (priceEl) priceEl.textContent = variant.priceDisplay;
    } else {
      compareEl?.closest('[role="group"]')?.remove();
      if (priceEl) priceEl.textContent = variant.priceDisplay;
      else {
        priceContainer.innerHTML = `<span class="price">${variant.priceDisplay}</span>`;
      }
    }
  }

  /**
   * Keeps the product-page discount badge in sync with the selected variant.
   * @param {HTMLElement} priceContainer
   * @param {VariantPriceData} variant
   */
  #setDiscountBadge(priceContainer, variant) {
    let badge = priceContainer.querySelector('.product-discount-badge');
    const percentage = Number(variant.discountPercentage ?? 0);

    if (!variant.showCompare || percentage <= 0) {
      badge?.remove();
      return;
    }

    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'product-discount-badge';
      badge.setAttribute('ref', 'discountBadge');
      priceContainer.appendChild(badge);
    }
    badge.textContent = `Save ${percentage}%`;
  }

  /**
   * @param {HTMLElement} priceContainer
   * @param {VariantPriceData} variant
   * @param {boolean} showSaleFirst
   */
  #setComparePrice(priceContainer, variant, showSaleFirst) {
    let compareEl = priceContainer.querySelector('.compare-at-price');
    const compareMarkup = `<span role="group">
      <span class="visually-hidden">Regular price</span>
      <span class="compare-at-price">${variant.compareDisplay}</span>
    </span>`;

    if (variant.showCompare) {
      if (!compareEl) {
        const priceEl = priceContainer.querySelector('.price, .cc-price-highlight');
        if (showSaleFirst && priceEl) {
          priceEl.insertAdjacentHTML('afterend', compareMarkup);
        } else {
          priceContainer.insertAdjacentHTML('afterbegin', compareMarkup);
        }
      } else {
        compareEl.textContent = variant.compareDisplay;
      }
    } else {
      compareEl?.closest('[role="group"]')?.remove();
    }
  }

  /**
   * Updates the price and volume pricing note from section HTML (background sync).
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    const newDataScript = newProductPrice.querySelector('script[ref="variantsPriceData"]');
    if (newDataScript instanceof HTMLScriptElement) {
      const existing = this.querySelector('script[ref="variantsPriceData"]');
      if (existing) {
        existing.replaceWith(newDataScript.cloneNode(true));
      } else {
        this.appendChild(newDataScript.cloneNode(true));
      }
      this.#loadVariantsMap();
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
